import {useEffect,useState} from 'react';
import {getRecoveryRedirectUrl} from '../lib/authRecovery';
import './AuthDialog.css';

function explainSignInError(error) {
  const message=error?.message || '';
  if (/invalid login credentials/i.test(message)) {
    return 'Email or password is incorrect. Check your details and try again.';
  }
  if (/email not confirmed/i.test(message)) {
    return 'This test account needs to be confirmed in Supabase before it can sign in.';
  }
  if (/too many requests|rate limit/i.test(message)) {
    return 'Too many sign-in attempts. Wait a little and try again.';
  }
  return 'Sign-in failed. Check your connection and account settings,then retry.';
}

function recoveryErrorCode(error) {
  if (typeof error?.code==='string' && /^[A-Z0-9_-]{2,80}$/i.test(error.code)) {
    return error.code;
  }
  const message=typeof error?.message==='string' ? error.message : '';
  return message.match(/\b[A-Z][A-Z0-9_]{3,}\b/)?.[0] || '';
}

function explainRecoveryError(error) {
  const message=error?.message || '';
  let explanation='The password recovery request could not be completed. Check your connection and try again.';
  if (/redirect|url/i.test(message)) {
    explanation='Supabase did not accept this redirect URL. Check that the exact CutScript recovery URL is in the allowed Redirect URLs list.';
  } else if (/session|token|expired|invalid/i.test(message)) {
    explanation='This recovery link is invalid or has expired. Request a new password reset.';
  }

  const code=recoveryErrorCode(error);
  return code ? `${explanation} Supabase error code: ${code}.` : explanation;
}

export default function AuthDialog({
  onClose,
  onSignIn,
  onSignUp,
  authError,
  clearAuthError,
  requestPasswordReset,
  updatePassword,
  recoveryMode,
  onRecoveryComplete
}) {
  const [view,setView]=useState(recoveryMode ? 'reset' : 'signin');
  const [email,setEmail]=useState('');
  const [password,setPassword]=useState('');
  const [confirmPassword,setConfirmPassword]=useState('');
  const [error,setError]=useState('');
  const [notice,setNotice]=useState('');
  const [redirectAllowed,setRedirectAllowed]=useState(false);
  const [redirectUrl,setRedirectUrl]=useState('');
  const [copied,setCopied]=useState(false);
  const [submitting,setSubmitting]=useState(false);
  const registrationEnabled=import.meta.env.VITE_CUTSCRIPT_SIGNUP_ENABLED==='true';

  useEffect(()=> {
    if (recoveryMode) setView('reset');
  },[recoveryMode]);

  const openForgotPassword=()=> {
    setError('');
    setNotice('');
    setRedirectAllowed(false);
    try {
      setRedirectUrl(getRecoveryRedirectUrl());
      setView('forgot');
    } catch (urlError) {
      setRedirectUrl('');
      setView('forgot');
      setError(urlError.message);
    }
  };

  const copyRedirectUrl=async ()=> {
    try {
      await navigator.clipboard.writeText(redirectUrl);
      setCopied(true);
    } catch {
      setCopied(false);
      setError('Copy was blocked by the browser. Select and copy the recovery URL shown below.');
    }
  };

  const submitSignIn=async event=> {
    event.preventDefault();
    setError('');
    clearAuthError();
    setSubmitting(true);
    try {
      await onSignIn(email,password);
      onClose();
    } catch (signInError) {
      setError(explainSignInError(signInError));
    } finally {
      setSubmitting(false);
    }
  };

  const submitRegister=async event=>{
    event.preventDefault();setError('');setNotice('');
    if(password.length<8){setError('Use a password with at least 8 characters.');return;}
    if(password!==confirmPassword){setError('Passwords do not match.');return;}
    setSubmitting(true);
    try{
      const result=await onSignUp(email,password);
      if(result.session){setNotice('Your account has been created. You are now signed in.');}
      else setNotice('Check your email for a confirmation link to finish creating your account.');
    }catch(e){setError(/already|registered|exists/i.test(e.message||'')?'Try signing in or resetting the password for that email.':'Could not create an account. Please retry.');}
    finally{setSubmitting(false);}
  };

  const submitResetRequest=async event=> {
    event.preventDefault();
    setError('');
    setNotice('');
    if (!redirectUrl || !redirectAllowed) {
      setError('Add the exact recovery URL to Supabase Redirect URLs and confirm it before sending a reset email.');
      return;
    }
    setSubmitting(true);
    try {
      await requestPasswordReset(email,redirectUrl);
      setNotice('If an account exists for that email,a password recovery message has been sent.');
    } catch (requestError) {
      setError(explainRecoveryError(requestError));
    } finally {
      setSubmitting(false);
    }
  };

  const submitNewPassword=async event=> {
    event.preventDefault();
    setError('');
    setNotice('');
    if (password.length < 8) {
      setError('Choose a password with at least 8 characters.');
      return;
    }
    if (password !==confirmPassword) {
      setError('The new passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await updatePassword(password);
      setNotice('Password updated. You can now sign in with your new password.');
      window.history.replaceState({},document.title,window.location.pathname);
      window.setTimeout(onRecoveryComplete,900);
    } catch (updateError) {
      setError(explainRecoveryError(updateError));
    } finally {
      setSubmitting(false);
    }
  };

  const close=()=> {
    if (recoveryMode) return;
    onClose();
  };

  return (
    <div className="auth-backdrop" onMouseDown={close}>
      <section
        className={`auth-dialog ${recoveryMode ? 'recovery-page' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-title"
        onMouseDown={event=> event.stopPropagation()}
      >
        {!recoveryMode && (
          <button className="auth-close" type="button" onClick={close} aria-label="Close sign-in"> × </button>
        )}
        <span className="auth-eyebrow">CUTSCRIPT ACCOUNT</span>

        {view==='signin' && (
          <>
            <h2 id="auth-title">Sign in</h2>
            <p>{registrationEnabled?'Sign in to access your projects and editing workspace.':'Use your CutScript beta account. Registration is currently invite-only.'}</p>
            {(error || authError) && <div className="auth-error" role="alert">{error || authError}</div>}
            <form onSubmit={submitSignIn}>
              <label>
                Email
                <input type="email" autoComplete="username" value={email} onChange={event=> setEmail(event.target.value)} required />
              </label>
              <label>
                Password
                <input type="password" autoComplete="current-password" value={password} onChange={event=> setPassword(event.target.value)} required />
              </label>
              <button className="auth-submit" type="submit" disabled={submitting}>
                {submitting ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
            <button className="auth-text-button" type="button" onClick={openForgotPassword}>Forgot password?</button>
            {registrationEnabled && <button className="auth-text-button" type="button" onClick={()=>{setView('register');setError('');setNotice('');}}>Create an account</button>}
          </>
        )}

        {view==='register' && registrationEnabled && (
          <>
            <h2 id="auth-title">Create your account</h2>
            <p>Start with an email address and a secure password. Email confirmation may be required.</p>
            {error && <div className="auth-error" role="alert">{error}</div>}
            {notice && <div className="auth-notice" role="status">{notice}</div>}
            <form onSubmit={submitRegister}>
              <label>Email<input type="email" autoComplete="email" value={email} onChange={e=>setEmail(e.target.value)} required/></label>
              <label>Password<input type="password" autoComplete="new-password" minLength="8" value={password} onChange={e=>setPassword(e.target.value)} required/></label>
              <label>Confirm password<input type="password" autoComplete="new-password" minLength="8" value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)} required/></label>
              <button className="auth-submit" disabled={submitting}>{submitting?'Creating account…':'Create account'}</button>
            </form>
            <button className="auth-text-button" type="button" onClick={()=>{setView('signin');setError('');setNotice('');}}>Already have an account? Sign in</button>
          </>
        )}

        {view==='forgot' && (
          <>
            <h2 id="auth-title">Reset your password</h2>
            <p>We’ll email a recovery link for your CutScript test account.</p>
            {(error || authError) && <div className="auth-error" role="alert">{error || authError}</div>}
            {notice && <div className="auth-notice" role="status">{notice}</div>}
            <form onSubmit={submitResetRequest}>
              <label>
                Email
                <input type="email" autoComplete="email" value={email} onChange={event=> setEmail(event.target.value)} required />
              </label>
              <div className="recovery-url">
                <strong>Add this exact URL to Supabase → Authentication → URL Configuration → Redirect URLs:</strong>
                <code>{redirectUrl || 'Open the hosted CutScript app to generate its recovery URL.'}</code>
                <button className="auth-text-button" type="button" onClick={copyRedirectUrl} disabled={!redirectUrl}>
                  {copied ? 'Copied' : 'Copy recovery URL'}
                </button>
              </div>
              <label className="recovery-confirm">
                <input type="checkbox" checked={redirectAllowed} onChange={event=> setRedirectAllowed(event.target.checked)} />
                I added this exact URL to the allowed Redirect URLs list.
              </label>
              <button className="auth-submit" type="submit" disabled={submitting || !redirectAllowed || !redirectUrl}>
                {submitting ? 'Sending…' : 'Send recovery email'}
              </button>
            </form>
            <button className="auth-text-button" type="button" onClick={()=> {setView('signin');setError('');setNotice('');}}>
              Back to sign in
            </button>
          </>
        )}

        {view==='reset' && (
          <>
            <h2 id="auth-title">Choose a new password</h2>
            <p>Enter and confirm a new password for your CutScript account.</p>
            {(error || authError) && <div className="auth-error" role="alert">{error || authError}</div>}
            {notice && <div className="auth-notice" role="status">{notice}</div>}
            <form onSubmit={submitNewPassword}>
              <label>
                New password
                <input type="password" autoComplete="new-password" minLength="8" value={password} onChange={event=> setPassword(event.target.value)} required />
              </label>
              <label>
                Confirm new password
                <input type="password" autoComplete="new-password" minLength="8" value={confirmPassword} onChange={event=> setConfirmPassword(event.target.value)} required />
              </label>
              <button className="auth-submit" type="submit" disabled={submitting}>
                {submitting ? 'Updating password…' : 'Update password'}
              </button>
            </form>
            {!recoveryMode && (
              <button className="auth-text-button" type="button" onClick={openForgotPassword}>Request a new recovery link</button>
            )}
          </>
        )}
      </section>
    </div>
  );
}
